export const WORKBENCH_SCHEMA_VERSION = 1 as const;

const SHA256_ROUND_CONSTANTS = new Uint32Array([
  0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
  0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
  0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
  0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2
]);
const rotateRight = (value: number, bits: number) => (value >>> bits) | (value << (32 - bits));
const toHex = (buffer: ArrayBuffer) => [...new Uint8Array(buffer)].map((value) => value.toString(16).padStart(2, "0")).join("");

function fallbackSha256(buffer: ArrayBuffer): string {
  const source = new Uint8Array(buffer), paddedLength = Math.ceil((source.byteLength + 9) / 64) * 64, padded = new Uint8Array(paddedLength); padded.set(source); padded[source.byteLength] = 0x80;
  const bitLength = source.byteLength * 8, view = new DataView(padded.buffer); view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000)); view.setUint32(paddedLength - 4, bitLength >>> 0);
  const hash = new Uint32Array([0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]), schedule = new Uint32Array(64);
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) schedule[index] = view.getUint32(offset + index * 4);
    for (let index = 16; index < 64; index += 1) { const a = schedule[index - 15], b = schedule[index - 2], small0 = rotateRight(a, 7) ^ rotateRight(a, 18) ^ (a >>> 3), small1 = rotateRight(b, 17) ^ rotateRight(b, 19) ^ (b >>> 10); schedule[index] = (schedule[index - 16] + small0 + schedule[index - 7] + small1) >>> 0; }
    let [a,b,c,d,e,f,g,h] = hash;
    for (let index = 0; index < 64; index += 1) { const large1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25), choose = (e & f) ^ (~e & g), temporary1 = (h + large1 + choose + SHA256_ROUND_CONSTANTS[index] + schedule[index]) >>> 0, large0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22), majority = (a & b) ^ (a & c) ^ (b & c), temporary2 = (large0 + majority) >>> 0; h = g; g = f; f = e; e = (d + temporary1) >>> 0; d = c; c = b; b = a; a = (temporary1 + temporary2) >>> 0; }
    hash[0] = (hash[0] + a) >>> 0; hash[1] = (hash[1] + b) >>> 0; hash[2] = (hash[2] + c) >>> 0; hash[3] = (hash[3] + d) >>> 0; hash[4] = (hash[4] + e) >>> 0; hash[5] = (hash[5] + f) >>> 0; hash[6] = (hash[6] + g) >>> 0; hash[7] = (hash[7] + h) >>> 0;
  }
  return [...hash].map((value) => value.toString(16).padStart(8, "0")).join("");
}

export async function sha256Hex(buffer: ArrayBuffer, subtle: Pick<SubtleCrypto, "digest"> | null | undefined = typeof crypto === "undefined" ? undefined : crypto.subtle): Promise<string> {
  return subtle ? toHex(await subtle.digest("SHA-256", buffer)) : fallbackSha256(buffer);
}

export type ExecutionLocation = "browser-worker" | "desktop-workbench" | "remote-worker";
export type RecipeStatus = "complete" | "cancelled" | "failed" | "imported";

export type AssetRecord = {
  id: string;
  mediaType: string;
  byteLength: number;
  sha256: string;
  storage: "opfs" | "metadata-only";
  createdAt: string;
};

export type OperationRecord = {
  id: string;
  toolId: string;
  version: number;
  execution: ExecutionLocation;
  inputAssetIds: string[];
  outputAssetIds: string[];
  parameters: Record<string, string | number | boolean | null>;
  startedAt: string;
  completedAt: string;
};

export type MeasurementReport = {
  kind: "stl-inspection";
  triangles: number;
  encoding: "ascii" | "binary";
  bounds: { min: number[]; max: number[]; size: number[] };
  surfaceArea: number;
  signedVolumeEstimate: number;
  warnings: string[];
};

export type RecipeRecord = {
  schemaVersion: typeof WORKBENCH_SCHEMA_VERSION;
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  status: RecipeStatus;
  assets: AssetRecord[];
  operations: OperationRecord[];
  reports: MeasurementReport[];
};

export type WorkbenchHandoff = {
  schemaVersion: typeof WORKBENCH_SCHEMA_VERSION;
  kind: "partharbor-workbench-handoff";
  requiredCapabilities: string[];
  recipe: RecipeRecord;
};

export function isRecipeRecord(value: unknown): value is RecipeRecord {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<RecipeRecord>;
  return candidate.schemaVersion === WORKBENCH_SCHEMA_VERSION &&
    typeof candidate.id === "string" &&
    typeof candidate.title === "string" &&
    Array.isArray(candidate.assets) &&
    Array.isArray(candidate.operations) &&
    Array.isArray(candidate.reports);
}

export function createWorkbenchHandoff(recipe: RecipeRecord): WorkbenchHandoff {
  return {
    schemaVersion: WORKBENCH_SCHEMA_VERSION,
    kind: "partharbor-workbench-handoff",
    requiredCapabilities: [...new Set(recipe.operations.map((operation) => operation.toolId))],
    recipe
  };
}

export type SupportCapabilityInput = {
  secureContext: boolean;
  crossOriginIsolated: boolean;
  webWorker: boolean;
  webAssembly: boolean;
  opfs: boolean;
  indexedDb: boolean;
  serviceWorker: boolean;
  webgl2: boolean;
  webgpu: boolean;
  fileSystemAccess: boolean;
  hardwareConcurrency?: number;
  deviceMemoryGiB?: number;
};
export type LocalBenchmarkResult = { iterations: number; elapsedMs: number; operationsPerSecond: number; checksum: number };
export type SupportPayload = {
  schemaVersion: 1;
  kind: "partharbor-local-support-preview";
  generatedAt: string;
  application: { name: "PartHarbor Tools"; version: string; utilities: number; indexableRoutes: number };
  capabilities: Omit<SupportCapabilityInput, "hardwareConcurrency" | "deviceMemoryGiB">;
  hardwareBuckets: { logicalProcessors: string; deviceMemoryGiB: string };
  benchmark?: LocalBenchmarkResult;
  excludedData: string[];
};

function bucket(value: number | undefined, limits: [number, string][], unavailable = "unavailable"): string {
  if (!Number.isFinite(value) || !value || value < 1) return unavailable;
  return limits.find(([limit]) => value <= limit)?.[1] ?? limits.at(-1)![1].replace(/^.*?\+$/, (match) => match);
}

export function createSupportPayload(input: SupportCapabilityInput, generatedAt: string, application: { version: string; utilities: number; indexableRoutes: number }, benchmark?: LocalBenchmarkResult): SupportPayload {
  const { hardwareConcurrency, deviceMemoryGiB, ...capabilities } = input;
  const coarseBenchmark = benchmark ? { ...benchmark, elapsedMs: Math.max(.5, Math.round(benchmark.elapsedMs * 2) / 2), operationsPerSecond: Math.round(benchmark.operationsPerSecond / 100_000) * 100_000 } : undefined;
  return {
    schemaVersion: 1,
    kind: "partharbor-local-support-preview",
    generatedAt,
    application: { name: "PartHarbor Tools", ...application },
    capabilities,
    hardwareBuckets: {
      logicalProcessors: bucket(hardwareConcurrency, [[2,"1–2"],[4,"3–4"],[8,"5–8"],[16,"9–16"],[Infinity,"17+"]]),
      deviceMemoryGiB: bucket(deviceMemoryGiB, [[2,"1–2"],[4,"3–4"],[8,"5–8"],[16,"9–16"],[Infinity,"17+"]])
    },
    benchmark: coarseBenchmark,
    excludedData: ["model bytes", "filenames and paths", "recipe and profile contents", "local storage contents", "cookies", "IP address", "raw user-agent string", "persistent identifiers"]
  };
}

export type JobManifestPartInput = { name: string; copies: number; material: string; profile: string; orientation: string; sourceSha256?: string; notes?: string };
export type JobManifest = { schemaVersion:1;kind:"partharbor-print-job-manifest";title:string;createdAt:string;parts:Array<JobManifestPartInput & { line:number }>;totals:{ distinctParts:number;copies:number };warnings:string[] };
const boundedText = (value:string,name:string,max:number) => { const clean=value.trim(); if(!clean) throw new Error(`${name} is required.`); if(clean.length>max) throw new Error(`${name} must be ${max} characters or fewer.`); return clean; };
export function createJobManifest(title:string,parts:JobManifestPartInput[],createdAt:string):JobManifest {
  if(parts.length<1||parts.length>50)throw new Error("A job manifest must contain 1–50 part lines.");
  const normalized=parts.map((part,index)=>{if(!Number.isInteger(part.copies)||part.copies<1||part.copies>999)throw new Error(`Part ${index+1} copies must be an integer from 1 through 999.`);const checksum=part.sourceSha256?.trim().toLowerCase();if(checksum&&!/^[a-f0-9]{64}$/.test(checksum))throw new Error(`Part ${index+1} SHA-256 must contain exactly 64 hexadecimal characters.`);return{line:index+1,name:boundedText(part.name,`Part ${index+1} name`,120),copies:part.copies,material:boundedText(part.material,`Part ${index+1} material`,80),profile:boundedText(part.profile,`Part ${index+1} profile`,120),orientation:boundedText(part.orientation,`Part ${index+1} orientation`,160),...(checksum?{sourceSha256:checksum}:{}),...(part.notes?.trim()?{notes:part.notes.trim().slice(0,500)}:{})};});
  return{schemaVersion:1,kind:"partharbor-print-job-manifest",title:boundedText(title,"Manifest title",160),createdAt,parts:normalized,totals:{distinctParts:normalized.length,copies:normalized.reduce((sum,part)=>sum+part.copies,0)},warnings:["This planning manifest does not embed model or G-code bytes.","Verify exact source hashes, slicer/profile revisions, printer state, and material lots before production."]};
}
